// client/src/components/custom/CustomCollectionDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Grid, Card, CardContent, CardActions,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch,
  Alert, CircularProgress, Chip, Snackbar, Rating, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';
import useAuth from '../../hooks/useAuth';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

// ─── Field renderer for display ───────────────────────────────────────────────

function fieldDisplayValue(field, value) {
  if (value === null || value === undefined || value === '') return null;
  if (field.type === 'checkbox') return value ? '✓' : null;
  if (field.type === 'rating') return `${'★'.repeat(value)}${'☆'.repeat(5 - value)}`;
  if (field.type === 'date') return new Date(value).toLocaleDateString();
  return String(value);
}

// ─── Dynamic field input for add/edit dialog ─────────────────────────────────

function FieldInput({ field, value, onChange }) {
  const handleChange = (val) => onChange(field.id, val);

  if (field.type === 'checkbox') {
    return (
      <FormControlLabel
        label={field.label}
        control={<Switch checked={!!value} onChange={e => handleChange(e.target.checked)} />}
      />
    );
  }
  if (field.type === 'rating') {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">{field.label}</Typography>
        <Rating
          value={Number(value) || 0}
          onChange={(_, v) => handleChange(v)}
        />
      </Box>
    );
  }
  if (field.type === 'select') {
    return (
      <FormControl size="small" fullWidth>
        <InputLabel>{field.label}</InputLabel>
        <Select
          label={field.label}
          value={value || ''}
          onChange={e => handleChange(e.target.value)}
        >
          {!field.required && <MenuItem value=""><em>None</em></MenuItem>}
          {(field.options || []).map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
  if (field.type === 'textarea') {
    return (
      <TextField
        label={field.label} size="small" fullWidth multiline rows={3}
        required={field.required}
        value={value || ''}
        onChange={e => handleChange(e.target.value)}
      />
    );
  }
  return (
    <TextField
      label={field.label} size="small" fullWidth
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      required={field.required}
      value={value || ''}
      onChange={e => handleChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
      InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
    />
  );
}

// ─── Add/Edit Dialog ─────────────────────────────────────────────────────────

function ItemDialog({ open, onClose, onSave, schema, item }) {
  const [fields, setFields] = useState({});
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState('');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (item) {
        setFields(item.fields || {});
        setRating(item.rating || 0);
        setNotes(item.notes || '');
        setPrice(item.price || '');
      } else {
        setFields({});
        setRating(0);
        setNotes('');
        setPrice('');
      }
      setError('');
    }
  }, [open, item]);

  const handleFieldChange = (fieldId, value) => {
    setFields(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSave = async () => {
    // Validate required fields
    const missing = (schema.fields || []).filter(f => f.required && !fields[f.id]);
    if (missing.length) {
      setError(`Required: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ fields, rating, notes, price });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{item ? 'Edit Item' : 'Add Item'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {(schema.fields || []).sort((a, b) => a.order - b.order).map(f => (
            <Grid item xs={12} sm={f.gridProps?.sm || 6} key={f.id}>
              <FieldInput
                field={f}
                value={fields[f.id] ?? ''}
                onChange={handleFieldChange}
              />
            </Grid>
          ))}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Price / Value" size="small" fullWidth
              value={price}
              onChange={e => setPrice(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary">Rating</Typography>
              <Rating value={rating} onChange={(_, v) => setRating(v || 0)} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes" size="small" fullWidth multiline rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={handleSave}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Update diff dialog ──────────────────────────────────────────────────────

function UpdateDiffDialog({ open, onClose, schema, adopted }) {
  if (!adopted?.schemaSnapshot) return null;
  const oldFields = adopted.schemaSnapshot.fields || [];
  const newFields = schema.fields || [];
  const oldIds = new Set(oldFields.map(f => f.id));
  const newIds = new Set(newFields.map(f => f.id));
  const added = newFields.filter(f => !oldIds.has(f.id));
  const removed = oldFields.filter(f => !newIds.has(f.id));
  const renamed = newFields.filter(f => {
    const old = oldFields.find(of => of.id === f.id);
    return old && old.label !== f.label;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schema Changes (v{adopted.currentVersion} → v{adopted.pendingVersion})</DialogTitle>
      <DialogContent>
        {added.length === 0 && removed.length === 0 && renamed.length === 0 && (
          <Typography color="text.secondary">No field changes detected.</Typography>
        )}
        {added.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="success.main">Added fields</Typography>
            {added.map(f => <Chip key={f.id} label={f.label} size="small" color="success" sx={{ mr: 0.5, mt: 0.5 }} />)}
          </Box>
        )}
        {removed.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="error.main">Removed fields</Typography>
            {removed.map(f => <Chip key={f.id} label={f.label} size="small" color="error" sx={{ mr: 0.5, mt: 0.5 }} />)}
          </Box>
        )}
        {renamed.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="warning.main">Renamed fields</Typography>
            {renamed.map(f => {
              const old = (adopted.schemaSnapshot?.fields || []).find(of => of.id === f.id);
              return (
                <Chip key={f.id} label={`${old?.label} → ${f.label}`} size="small" color="warning" sx={{ mr: 0.5, mt: 0.5 }} />
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

export default function CustomCollectionDashboard({ isWishlist = false }) {
  const { schemaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { featureFlags } = useFeatureFlags();

  const [schema, setSchema] = useState(null);
  const [adopted, setAdopted] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [schemaRes, itemsRes] = await Promise.all([
        axiosInstance.get(`/custom-schemas/${schemaId}`),
        axiosInstance.get(`/custom-collections/${schemaId}/items/user/${user.id}`),
      ]);
      setSchema(schemaRes.data);
      setItems(itemsRes.data);

      // Try to get adoption record for update/removal banners
      const adoptedRes = await axiosInstance.get('/custom-schemas/adopted').catch(() => ({ data: [] }));
      const myAdoption = adoptedRes.data.find(a => a.schemaId === schemaId || a.schemaId?._id === schemaId || a.schemaId?.toString() === schemaId);
      setAdopted(myAdoption || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  }, [schemaId, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!featureFlags?.enableCustomCollections) {
    return (
      <div>
        <AppBarHeader hideCollectionNav />
        <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">Custom Collections is not enabled.</Typography>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <AppBarHeader />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div>
        <AppBarHeader />
        <Container maxWidth="sm" sx={{ mt: 4 }}>
          <Alert severity="error">{error || 'Schema not found'}</Alert>
        </Container>
      </div>
    );
  }

  // Build filter options from filterField values in items
  const filterFieldDef = schema.fields?.find(f => f.id === schema.filterField);
  const filterOptions = filterFieldDef
    ? [...new Set(items.map(it => it.fields?.[schema.filterField]).filter(Boolean))]
    : [];

  // Apply filter
  const displayItems = filter
    ? items.filter(it => it.fields?.[schema.filterField] === filter)
    : items;

  // Subtitle + detail from schema config
  const getSubtitle = (item) => {
    return (schema.subtitleFields || [])
      .map(fid => {
        const fDef = schema.fields?.find(f => f.id === fid);
        return fDef ? fieldDisplayValue(fDef, item.fields?.[fid]) : null;
      })
      .filter(Boolean)
      .join(' · ');
  };

  const getDetail = (item) => {
    return (schema.detailFields || [])
      .map(fid => {
        const fDef = schema.fields?.find(f => f.id === fid);
        return fDef ? fieldDisplayValue(fDef, item.fields?.[fid]) : null;
      })
      .filter(Boolean)
      .join(' · ');
  };

  // Primary display name — first text/number field or first field
  const getItemName = (item) => {
    const firstField = schema.fields?.[0];
    if (!firstField) return 'Item';
    return item.fields?.[firstField.id] || 'Untitled';
  };

  const handleAdd = async (data) => {
    const res = await axiosInstance.post(`/custom-collections/${schemaId}/items`, data);
    setItems(prev => [...prev, res.data]);
    showSnack('Item added');
  };

  const handleEdit = async (data) => {
    const res = await axiosInstance.put(`/custom-collections/${schemaId}/items/${editItem._id}`, data);
    setItems(prev => prev.map(it => it._id === editItem._id ? res.data : it));
    showSnack('Item updated');
  };

  const handleDelete = async () => {
    await axiosInstance.delete(`/custom-collections/${schemaId}/items/${deleteItem._id}`);
    setItems(prev => prev.filter(it => it._id !== deleteItem._id));
    setDeleteItem(null);
    showSnack('Item deleted');
  };

  const handleApplyUpdate = async () => {
    try {
      const res = await axiosInstance.post(`/custom-schemas/${schemaId}/apply-update`);
      setAdopted(res.data);
      fetchData();
      showSnack('Schema updated');
    } catch {
      showSnack('Failed to apply update', 'error');
    }
  };

  const handleDismissUpdate = async () => {
    try {
      const res = await axiosInstance.post(`/custom-schemas/${schemaId}/dismiss-update`);
      setAdopted(res.data);
    } catch {
      showSnack('Failed to dismiss update', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axiosInstance.get(`/custom-collections/${schemaId}/items/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${schema.name.replace(/\s+/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnack('Export failed', 'error');
    }
  };

  const isOwner = user && schema.ownerId && (schema.ownerId === user.id || schema.ownerId?._id === user.id || schema.ownerId?.toString() === user.id);
  const isRemoved = schema.isRemovedFromLibrary && adopted && adopted.status === 'active';

  return (
    <div>
      <AppBarHeader />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        {/* Update banner */}
        {adopted?.pendingVersion && (
          <Alert severity="info" sx={{ mb: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" color="inherit" onClick={() => setDiffOpen(true)}>View changes</Button>
                <Button size="small" color="inherit" variant="outlined" onClick={handleApplyUpdate}>Apply</Button>
                <Button size="small" color="inherit" onClick={handleDismissUpdate}>Dismiss</Button>
              </Box>
            }
          >
            The creator updated this schema.
          </Alert>
        )}

        {/* Removal banner */}
        {isRemoved && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This schema was removed from the community library. Your collection data is safe.
            <Button size="small" sx={{ ml: 1 }} onClick={handleExport}>Export data</Button>
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {schema.icon && <Typography variant="h4">{schema.icon}</Typography>}
            <Box>
              <Typography variant="h5" fontWeight={700}>{schema.name}</Typography>
              {schema.description && (
                <Typography variant="body2" color="text.secondary">{schema.description}</Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Export CSV">
              <IconButton onClick={handleExport}><DownloadIcon /></IconButton>
            </Tooltip>
            {isOwner && (
              <Button variant="outlined" size="small" onClick={() => navigate(`/custom-schemas/${schemaId}/edit`)}>
                Edit Schema
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditItem(null); setDialogOpen(true); }}>
              Add Item
            </Button>
          </Box>
        </Box>

        {/* Filter */}
        {filterOptions.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`All (${items.length})`}
              onClick={() => setFilter('')}
              color={!filter ? 'primary' : 'default'}
              variant={!filter ? 'filled' : 'outlined'}
            />
            {filterOptions.map(opt => (
              <Chip
                key={opt}
                label={opt}
                onClick={() => setFilter(opt === filter ? '' : opt)}
                color={filter === opt ? 'primary' : 'default'}
                variant={filter === opt ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        )}

        {/* Items grid */}
        {displayItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography color="text.secondary">No items yet. Add your first item!</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {displayItems.map(item => {
              const subtitle = getSubtitle(item);
              const detail = getDetail(item);
              return (
                <Grid item xs={12} sm={6} md={4} key={item._id}>
                  <Card sx={{
                    height: '100%', display: 'flex', flexDirection: 'column',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 3,
                  }}>
                    <CardContent sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>{getItemName(item)}</Typography>
                      {subtitle && (
                        <Typography variant="caption" color="text.secondary" display="block">{subtitle}</Typography>
                      )}
                      {detail && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{detail}</Typography>
                      )}
                      {item.rating > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', px: 1, pb: 1 }}>
                      <IconButton size="small" onClick={() => { setEditItem(item); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteItem(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* Add/Edit dialog */}
      <ItemDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={editItem ? handleEdit : handleAdd}
        schema={schema}
        item={editItem}
      />

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteItem)} onClose={() => setDeleteItem(null)}>
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <Typography>Delete "{deleteItem ? getItemName(deleteItem) : ''}"? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteItem(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Update diff dialog */}
      {schema && adopted && (
        <UpdateDiffDialog
          open={diffOpen}
          onClose={() => setDiffOpen(false)}
          schema={schema}
          adopted={adopted}
        />
      )}

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity}>{snack.msg}</Alert>
      </Snackbar>
    </div>
  );
}
